// © Microsoft Corporation. All rights reserved.

import { AppType } from "@msnews/experiences-constants";
import { PivotSwitchInfo } from "./Interfaces";
import { appEnvironment } from "@msnews/core";
import { CommonHeaderInvalidConfig } from "@msnews/diagnostics";
import { Telemetry } from "@msnews/telemetry-contracts";
import { PeregrineRouterConfig, PeregrineRouteConfig } from "./PeregrineRouterConfig";

/** Flag will be set to true if we parse a PeregrineRouterConfig where enableLocaleFeedPrefix is true */
export let enableLocaleFeedPrefix = false;

/**
 * Sets the enableLocaleFeedPrefix value
 * @param newEnableLocaleFeedPrefix new value for enableLocaleFeedPrefix
 */
export function updateEnableLocaleFeedPrefix(newEnableLocaleFeedPrefix: boolean): void {
    enableLocaleFeedPrefix = newEnableLocaleFeedPrefix;
}

/**
 * Formats route path
 * @param canonicalName canonical name for topic. If not an existing canonical name, then pass in the display string for the topic
 * @param id id for topic
 * @param usePrefix value to deactivate adding a prefix if unwanted. Default value is true and prefix is controlled by enableLocaleFeedPrefix
 * @param excludeFeedPrefix flag do disable including /feed in prefix
 */
export function getRoutePath(canonicalName: string, id?: string, usePrefix = true, excludeFeedPrefix = false): string {
    if (enableLocaleFeedPrefix && usePrefix) {
        let prefix = `/${appEnvironment.CurrentMarket}`;
        if (appEnvironment.AppType === AppType.Windows && !excludeFeedPrefix) {
            prefix += "/feed";
        }
        if (!canonicalName) {
            return prefix;
        }

        if (id) {
            return prefix + `/interest/${encodeURIComponent(canonicalName)}/${encodeURIComponent(id)}`;
        }

        const routeName = canonicalName[0] === "/" ? canonicalName.slice(1) : canonicalName;
        return prefix + (CanonicalNamePathMap[routeName] ? `${CanonicalNamePathMap[routeName]}` : `/${routeName}`);
    } else {
        if (!canonicalName) {
            return "";
        }

        if (id) {
            return `/interest/${encodeURIComponent(canonicalName)}/${encodeURIComponent(id)}`;
        }

        const routeName = canonicalName[0] === "/" ? canonicalName.slice(1) : canonicalName;
        return CanonicalNamePathMap[routeName] || `/${routeName}`;
    }
}

/**
 * Helper to check if the pivotSwitchInfo object is valid (has the pivot id)
 * @param pivotSwitchInfo pivot switch info object
 */
export function validatePivotSwitchInfo(pivotSwitchInfo: PivotSwitchInfo): boolean {
    if (!pivotSwitchInfo || !pivotSwitchInfo.oldSelection || !pivotSwitchInfo.newSelection) {
        return false;
    }

    if (!(pivotSwitchInfo.oldSelection && pivotSwitchInfo.oldSelection.pivotId)) {
        return false;
    }

    if (!(pivotSwitchInfo.newSelection && pivotSwitchInfo.newSelection.pivotId)) {
        return false;
    }

    return true;
}

/**
 * Get the relative routes path from external URL
 * @param externalUrl An anbsolute URL string
 */
export function getRoutePathFromExternalUrl(externalUrl: string) {
    try {
        return (new URL(externalUrl)).pathname;
    } catch {
        return null;
    }
}

/**
 * Consts for the route names
 */
export enum RouteNames {
    MyFeed = "myFeed",
    Personalize = "personalize",
    Saves = "saves"
}

/**
 * Map of canonical names to routes
 */
export const CanonicalNamePathMap: { [key: string]: string } = {
    myFeed: "/",
    personalize: "/personalize",
    interest: "/interest/*/:id",
    saves: "/personalize/saves"
};

/**
 * Get peregrine router config from common header config
 * @param config common header navigation config
 */
export function getPeregrineRouterConfig(config): PeregrineRouterConfig {
    if (!config || !config.navigationConfig) {
        Telemetry.sendAppErrorEvent({
            ...CommonHeaderInvalidConfig,
            message: "Config is missing or config is missing navigationConfig"
        });
        return null;
    }

    const { navigationConfig } = config;
    if (!navigationConfig.hamburgerMenuItems
        || !navigationConfig.hamburgerMenuItems.length
        || !navigationConfig.homeNavigationItem
        || !navigationConfig.contextualNavItems
        || !navigationConfig.contextualNavItems.length) {
        Telemetry.sendAppErrorEvent({
            ...CommonHeaderInvalidConfig,
            message: "Config is missing nav items. hamburgerMenuItems, homeNavigationItem, and/or contextualNavItems is misconfigured"
        });
        return;
    }

    const { hamburgerMenuItems, homeNavigationItem, contextualNavItems, interestsNavigationItem } = navigationConfig;
    const routes: PeregrineRouteConfig[] = [];

    // Add routes from the hamburger menu
    hamburgerMenuItems.forEach(menuItem => {
        routes.push(convertNavigationItemToPeregrineRoute(menuItem));

        if (menuItem.children && menuItem.children.length) {
            menuItem.children.map(child => routes.push(convertNavigationItemToPeregrineRoute(child)));
        }
    });

    // Add routes from the contextual nav, while not duplicating any routes
    tryPushToRoutes(routes, homeNavigationItem);
    contextualNavItems.forEach(item => {
        tryPushToRoutes(routes, item);

        if (item.children && item.children.length) {
            item.children.forEach(child => {
                tryPushToRoutes(routes, child);

                if (child.children && child.children.length) {
                    child.children.map(grandchild => tryPushToRoutes(routes, grandchild));
                }
            });
        }
    });

    // Add routes from the interests nav item if it exists, while not duplicating any routes
    tryPushToRoutes(routes, interestsNavigationItem);
    if (interestsNavigationItem && interestsNavigationItem.children && interestsNavigationItem.children.length) {
        interestsNavigationItem.children.forEach(child => {
            tryPushToRoutes(routes, child);
        });
    }

    return {
        staticRoutes: routes,
        disableLoadingDynamicRoutes: true,
        enableLocaleFeedPrefix: config.navigationConfig.enableLocaleFeedPrefix
    };
}

/**
 * Method to push a navigation item to the routes array, given that it isn't already part of the array
 * @param routes array of routes to push to
 * @param item item trying to push to array
 */
export function tryPushToRoutes(routes: PeregrineRouteConfig[], item): void {
    if (!routes || !routes.length || !item || routes.some(route => route && route.pivotId === item.id)) {
        return;
    }
    routes.push(convertNavigationItemToPeregrineRoute(item));
}

/**
 * Converts navigation item to peregrine route
 * @param item navigation item
 */
export function convertNavigationItemToPeregrineRoute(item): PeregrineRouteConfig {
    if (!item || !item.renderInfo) {
        return;
    }

    // Create experience info based on whether it's a git config or cms config
    let experienceInfo;
    if (item.renderInfo.experienceConfigHref) {
        experienceInfo = {
            href: item.renderInfo.experienceConfigHref,
            instanceId: item.id
        };
    } else if (item.renderInfo.experienceConfigRef) {
        experienceInfo = {
            ...item.renderInfo.experienceConfigRef,
            instanceId: item.id
        };
    }

    return {
        externalUrl: item.renderInfo.externalUrl,
        experienceInfo,
        canonicalName: item.renderInfo.path && (item.renderInfo.usePathNameAsIs ? item.renderInfo.path : `interest/${item.renderInfo.path}`),
        telemetryPageName: item.telemetryInfo && item.telemetryInfo.pageName,
        telemetryPivotCategory: item.telemetryInfo && item.telemetryInfo.pivotCategory,
        pivotId: item.id,
        context: item.renderInfo.context,
        display: item.display,
        categoryKey: item.categoryKey,
        subCategoryKey: item.subCategoryKey,
        pageTitle: item.pageTitle
    };
}
